/**
 * Customer-facing wording for how much of the documents' content the agents can use.
 *
 * The line sits under the file-count and total-size quotas, so it MUST name what it
 * measures or it reads as a third limit on the same two things ("Content: 95%" next to
 * "10 of 10 documents" and "12 / 100 MB" is unreadable). It measures neither: it is how
 * much of what the Regara AI can take in per run these documents already fill. "Regara AI
 * context" is the card's own phrasing ("give the Regara AI more context to work with"),
 * which keeps it honest without units, tokens, or any mention of the model. Pure.
 */

export interface ContentCapacityCopy {
  /** The line shown under the quota bar. */
  text: string;
  /** Over the limit — agents will refuse to run. */
  over: boolean;
  /** 90% and up, not yet over. */
  nearlyFull: boolean;
  /** Exact share used, 0..∞ (1 = exactly at the limit). */
  ratio: number;
}

export function contentCapacityCopy(totalChars: number, budgetChars: number): ContentCapacityCopy {
  const ratio = budgetChars > 0 ? totalChars / budgetChars : 0;
  const pct = Math.round(ratio * 100);
  if (ratio > 1) {
    return {
      text: 'Regara AI context: over the limit. Remove or shorten documents to run the agents.',
      over: true,
      nearlyFull: false,
      ratio,
    };
  }
  if (ratio >= 0.9) {
    return {
      text: `Regara AI context: ${pct}% used — nearly full.`,
      over: false,
      nearlyFull: true,
      ratio,
    };
  }
  return { text: `Regara AI context: ${pct}% used`, over: false, nearlyFull: false, ratio };
}

/** Hover text on a document's size: an approximate length, never on the face of the page. */
export function approxLengthTitle(chars: number): string {
  const pages = Math.max(1, Math.round(chars / 3000));
  return `About ${pages} page${pages === 1 ? '' : 's'} of text`;
}
